import {useLazyQuery} from "@apollo/client";
import {useCallback, useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {useDebounce} from "use-debounce";

import {ServiceField} from "@/lib/constants/service.ts";
import {DNSLookUpResponse, GQL_DNS_LOOKUP} from "@/lib/queries/checks.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";
import {TextWithButton} from "@/pages/checks/components/form/fields/TextWithButton.tsx";

interface DNSAutoloadFieldProps extends FieldProps {}

export const DNSAutoloadField = ({className, name, label, tooltip, required}: DNSAutoloadFieldProps) => {
  const {setValue, trigger, clearErrors, watch, setError} = useFormContext();
  const [dnsLookup] = useLazyQuery<DNSLookUpResponse>(GQL_DNS_LOOKUP);
  const [searchDebounced] = useDebounce(watch(ServiceField.ADDRESS), 600);
  const expectString = watch(ServiceField.EXPECT_STRING);

  const handleAutoload = useCallback(async () => {
    await trigger(ServiceField.ADDRESS);
    const domain = watch(ServiceField.ADDRESS);
    if (!domain) {
      return;
    }

    clearErrors([ServiceField.EXPECT_STRING, ServiceField.DNS_SERVER]);
    const {data, error} = await dnsLookup({
      variables: {
        domain: domain,
        recordType: watch(ServiceField.DNS_RECORD_TYPE),
        nameserver: watch(ServiceField.DNS_SERVER),
      },
      fetchPolicy: "no-cache",
    });
    if (error) {
      console.error(error);
    }
    if (data?.dnsLookup?.errors) {
      Object.keys(data?.dnsLookup?.errors).forEach((key) => {
        setError(key, {message: data?.dnsLookup?.errors[key][0]});
      });
      return;
    }

    if (data?.dnsLookup?.dnsServer) {
      setValue(ServiceField.DNS_SERVER, data?.dnsLookup?.dnsServer, {shouldDirty: true, shouldValidate: true});
    }
    if (data?.dnsLookup?.expectString) {
      setValue(ServiceField.EXPECT_STRING, data?.dnsLookup?.expectString, {shouldDirty: true, shouldValidate: true});
    }
  }, [trigger, watch, clearErrors, setValue, setError, dnsLookup]);

  useEffect(() => {
    if (searchDebounced && !expectString) {
      handleAutoload();
    }
  }, [searchDebounced, handleAutoload, expectString]);

  return (
    <TextWithButton
      className={className}
      btnTitle="Autoload"
      bntOnClick={handleAutoload}
      name={name}
      label={label}
      helpText={tooltip}
      required={required}
      variant="split"
    />
  );
};
